<template>
    <div class="table-action / flex" v-bind="{[`${!actionOptions.length ? 'data-no-actions' : 'data-actions'}`]: ''}">
        <span v-if="!actionOptions.length" :key="rowId">{{ '-' }}</span>
        <NuxtLink
            v-else-if="actionOptions.length === 1"
            :to="actionOptions[0].href"
            :external="actionOptions[0].external"
            class="table-action / flex"
            @click.stop
        >
            <span v-if="!isOverflowing" ref="cellContent" class="table-action_button single-action">{{ actionOptions[0].label }}</span>
            <VJoyTooltip v-else variant="secondary" class="joy-tooltip" placement="bottom">
                <span ref="cellContent" class="table-action__single-action">{{ actionOptions[0].label }}</span>
                <template #tooltip-content>
                    {{ actionOptions[0].label }}
                </template>
            </VJoyTooltip>
        </NuxtLink>
        <div v-else ref="tableActionRef" href="#" class="table-action_button multiple-actions / flex">
            <VJoyDropdown
                :options="actionOptions"
                :direction="dropdownDirection"
                width="fit-content"
                @dropdown:open="handleDropdownOpen"
                @dropdown:hide="handleDropdownClose"
            >
                <template #dropdown-button>
                    <div class="table-action__dropdown-button / flex">
                        <p>{{ actionOptions[0].label }}</p>
                        <VJoyIcon name="chevron-down" size="xxsmall" />
                    </div>
                </template>
                <template #dropdown-item="item">
                    <NuxtLink class="table-action__dropdown-link" :to="item.href ?? ''" :external="item.external" @click.stop>
                        {{ item.label }}
                    </NuxtLink>
                </template>
            </VJoyDropdown>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed, ref, useTemplateRef, watch, type Ref} from 'vue';
    import {useUserStore} from '@common/stores';
    import {helpers} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {useGuestHost, useTranslation} from '#imports';
    import {useContentOverflow} from '@malt/utils';
    import {VJoyDropdown, VJoyIcon, VJoyTooltip} from '@maltjoy/core-vue';
    import {ClientProjectType, ProjectClientAction} from '@api';
    import {useProjectsListStore} from '../../stores';

    const {t} = useTranslation();

    interface ActionOption {
        id: ProjectClientAction | string;
        label: string;
        href: string;
        external?: boolean;
    }

    const props = defineProps<{
        actions: ProjectClientAction[];
        rowId: string;
        type?: ClientProjectType;
        paymentIds?: Array<any>;
        batchIds?: Array<any>;
        correlationId?: string;
    }>();

    const tableActionRef = useTemplateRef('tableActionRef');

    const cellContent = useTemplateRef('cellContent');

    const {isOverflowing, startObserving, stopObserving} = useContentOverflow({root: cellContent as Ref<HTMLElement | undefined>, immediate: false});

    watch(
        isOverflowing,
        () => {
            stopObserving();
            startObserving();
        },
        {immediate: true},
    );

    // project list store
    const projectListStore = useProjectsListStore();
    const {formattedResults} = storeToRefs(projectListStore);

    const userStore = useUserStore();
    const {isPlatformAdmin} = storeToRefs(userStore);
    const host = useGuestHost();

    const projectDetailUrl = computed(() => {
        return props.actions.map((action) => {
            if (action === ProjectClientAction.CompleteLot || action === ProjectClientAction.CompleteMission) {
                return helpers.path(`/project/${props.rowId}/validation`) as unknown as string;
            }

            if (action === ProjectClientAction.ApproveExpense) {
                return helpers.path(`/project/${props.rowId}/expense-validation`) as unknown as string;
            }

            if (action === ProjectClientAction.PayExpense && props.paymentIds?.length) {
                return `${host}/project-payment/${props.paymentIds[0]}`;
            }

            if (action === ProjectClientAction.ReviewExpense) {
                return helpers.path(`/project/${props.rowId}/expense-list`) as unknown as string;
            }

            if (action === ProjectClientAction.ApproveExpenseBatch && props.batchIds?.length) {
                return `/project/${props.rowId}/expense-validation?batch=${props.batchIds[0]}`;
            }

            if (action === ProjectClientAction.PrepayFixedPriceMission) {
                return `${host}/project-payment/${props.rowId}`;
            }

            if (action === ProjectClientAction.AddPurchaseOrder) {
                return helpers.path(`/project/${props.rowId}/details`) as unknown as string;
            }

            if (action === ProjectClientAction.LetFreelanceAppraisal) {
                return `${host}/appraisal/freelancer-by-client/${props.rowId}?origin=todo-table`;
            }

            if (props.type === ClientProjectType.CompletedProject) {
                return helpers.path(`/project/${props.rowId}`);
            } else {
                return `${host}/project-client/quote/${props.rowId}`;
            }
        });
    });

    const tableActionLabel = computed(() => {
        return props.actions.map((action) => {
            switch (action) {
                case ProjectClientAction.AcceptProposal:
                    return t(`projectclient.projectlist.table.action.value.${ProjectClientAction.AcceptProposal}`);
                case ProjectClientAction.AcceptAmendment:
                    return t(`projectclient.projectlist.table.action.value.${ProjectClientAction.AcceptAmendment}`);
                case ProjectClientAction.AddPurchaseOrder:
                    return t(`projectclient.projectlist.table.action.value.${ProjectClientAction.AddPurchaseOrder}`);
                case ProjectClientAction.PrepayFixedPriceMission:
                    return t(`projectclient.projectlist.table.action.value.${ProjectClientAction.PrepayFixedPriceMission}`);
                case ProjectClientAction.PayMission:
                    return t(`projectclient.projectlist.table.action.value.${ProjectClientAction.PayMission}`);
                case ProjectClientAction.CompleteMission:
                    return t(`projectclient.projectlist.table.action.value.${ProjectClientAction.CompleteMission}`);
                case ProjectClientAction.LetFreelanceAppraisal:
                    return t(`projectclient.projectlist.table.action.value.${ProjectClientAction.LetFreelanceAppraisal}`);
                case ProjectClientAction.CompleteLot:
                    return t(`projectclient.projectlist.table.action.value.${ProjectClientAction.CompleteLot}`);
                case ProjectClientAction.ApproveExpense:
                    return t(`projectclient.projectlist.table.action.value.${ProjectClientAction.ApproveExpense}`);
                case ProjectClientAction.PayExpense:
                    return t(`projectclient.projectlist.table.action.value.${ProjectClientAction.PayExpense}`);
                case ProjectClientAction.ReviewExpense:
                    return t(`projectclient.projectlist.table.action.value.${ProjectClientAction.ReviewExpense}`);
                case ProjectClientAction.ApproveExpenseBatch:
                    // TODO: change label of review batch expense action to appropriate name
                    return t(`projectclient.projectlist.table.action.value.${ProjectClientAction.ReviewExpense}`);
                default:
                    return '-';
            }
        });
    });

    const actionOptions = computed(() => {
        const baseOptions: ActionOption[] = props.actions.map((action, index) => ({
            id: action,
            label: tableActionLabel.value[index],
            href: projectDetailUrl.value[index] as string,
        }));

        if (isPlatformAdmin.value && props.correlationId) {
            baseOptions.push({
                id: 'new-admin-project-view',
                label: 'Access Admin project',
                href: `/project-admin/project/${props.correlationId}`,
                external: true,
            });
        }

        return baseOptions;
    });

    const dropdownDirection = ref<'down' | 'up'>('down');

    function handleDropdownOpen() {
        if (tableActionRef.value) {
            const parentTD = tableActionRef.value.closest<HTMLTableCellElement>('.data-table-column');
            if (parentTD) {
                parentTD.style.zIndex = '1000';
                // open droptown to the top for last item in the list to prevent it from being hidden behind the footer
                if (parentTD.closest('tr')?.rowIndex === formattedResults.value.length && formattedResults.value.length > 5) {
                    dropdownDirection.value = 'up';
                }
            }
        }
    }
    function handleDropdownClose() {
        if (tableActionRef.value) {
            const parentTD = tableActionRef.value.closest<HTMLTableCellElement>('.data-table-column');
            if (parentTD) {
                parentTD.style.zIndex = '0';
            }
        }
    }
</script>

<style lang="scss" scoped>
    .table-action {
        height: 100%;
        align-items: center;
        justify-content: center;

        &_button {
            background: var(--joy-color-background-default);
            border-radius: var(--joy-core-spacing-2);
            padding: var(--joy-core-spacing-2) var(--joy-core-spacing-3);
            max-width: fit-content;
            transition: background var(--joy-transition-duration-long);

            &:hover,
            &:focus {
                background: var(--joy-color-secondary-10);
            }

            &.single-action {
                color: var(--joy-color-secondary-50);
            }

            &.multiple-actions {
                flex-flow: row nowrap;
            }
        }

        &__dropdown-button {
            align-items: center;
            flex-wrap: nowrap;
            color: var(--joy-color-secondary-50);

            & > p {
                margin-bottom: 0;
                margin-right: 5px;
            }
        }

        &__dropdown-link {
            color: var(--joy-color-secondary-50);
            text-decoration: none !important;
        }
    }

    :deep(.joy-dropdown__list) {
        right: 0;
        left: unset;
    }
</style>
